import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckSquare, Square, ListChecks } from 'lucide-react'
import type { Account, ActionItem } from '@/types/entities'
import { useStore } from '@/store/useStore'
import { SectionCard, EmptyState, DataTable, Badge, type Column } from '@/components/ui'
import { date, daysUntil, todayIso} from '@/lib/format'
import type { AccountDerived } from '../lib'
import { priorityTone, sortByPriority } from '../lib'

export default function ActionItemsTab({ account, d }: { account: Account; d: AccountDerived }) {
  const navigate = useNavigate()
  const patch = useStore((s) => s.patch)
  const allItems = useStore((s) => s.data.actionItems)

  // All items (open + closed) scoped to this account / its tracks / properties,
  // so toggling to Closed keeps the row visible (struck through) instead of vanishing.
  const trackIds = new Set(d.tracks.map((t) => t.id))
  const propIds = d.propIds
  const rows = useMemo(() => {
    const scoped = allItems.filter((a) => {
      const r = a.attachedTo
      if (r.kind === 'account' && r.id === account.id) return true
      if (r.kind === 'track' && trackIds.has(r.id)) return true
      if (r.kind === 'property' && propIds.has(r.id)) return true
      return false
    })
    // open first (by priority), then closed.
    const open = sortByPriority(scoped.filter((a) => a.status !== 'Closed'))
    const closed = scoped.filter((a) => a.status === 'Closed')
    return [...open, ...closed]
  }, [allItems, account.id, trackIds, propIds])

  function toggle(item: ActionItem) {
    patch('actionItems', item.id, item.status === 'Closed'
      ? { status: 'Open', closedAt: undefined }
      : { status: 'Closed', closedAt: todayIso() })
  }

  if (!rows.length) {
    return (
      <SectionCard pad>
        <EmptyState icon={<ListChecks size={36} />} title="No action items" hint="Open follow-ups from reviews, reporting, and operations will appear here. Check an item to close it." />
      </SectionCard>
    )
  }

  const columns: Column<ActionItem>[] = [
    {
      key: 'done', header: '', width: 40, align: 'center',
      render: (a) => (
        <button
          className="aw-check"
          onClick={(e) => { e.stopPropagation(); toggle(a) }}
          aria-label={a.status === 'Closed' ? 'Reopen' : 'Close'}
        >
          {a.status === 'Closed' ? <CheckSquare size={17} className="aw-check-on" /> : <Square size={17} />}
        </button>
      ),
    },
    {
      key: 'title', header: 'Item', sortValue: (a) => a.title,
      render: (a) => (
        <div className={`aw-cell-stack${a.status === 'Closed' ? ' aw-ai-closed' : ''}`}>
          <span className="aw-ai-title">{a.title}</span>
          {a.attachedTo.label && <span className="aw-cell-sub">{a.attachedTo.label}</span>}
        </div>
      ),
    },
    {
      key: 'priority', header: 'Priority', width: 100, align: 'center',
      sortValue: (a) => a.priority,
      render: (a) => <Badge tone={priorityTone(a.priority)} variant="soft">{a.priority}</Badge>,
    },
    {
      key: 'owner', header: 'Owner', width: 140, sortValue: (a) => a.owner ?? '',
      render: (a) => <span className="aw-muted-cell">{a.owner ?? '—'}</span>,
    },
    {
      key: 'due', header: 'Due', width: 130, align: 'right', sortValue: (a) => a.dueDate ?? '',
      render: (a) => {
        if (!a.dueDate) return <span className="dim">—</span>
        const du = daysUntil(a.dueDate)
        const late = a.status !== 'Closed' && du != null && du < 0
        return (
          <span className={`num${late ? ' aw-due-late' : ''}`}>
            {date(a.dueDate, 'short')}
            {a.status !== 'Closed' && du != null && <span className="aw-due-rel">{du < 0 ? `${-du}d late` : `${du}d`}</span>}
          </span>
        )
      },
    },
    {
      key: 'source', header: 'Source', width: 150, sortValue: (a) => a.source ?? '',
      render: (a) => <span className="aw-muted-cell">{a.source ?? '—'}</span>,
    },
  ]

  return (
    <SectionCard
      eyebrow="Follow-ups"
      title={<>Action <b>Items</b></>}
      right={<span className="aw-count-pill">{rows.filter((r) => r.status !== 'Closed').length} open</span>}
      pad={false}
    >
      <DataTable<ActionItem>
        columns={columns}
        rows={rows}
        getRowKey={(a) => a.id}
        onRowClick={(a) => { if (a.attachedTo.kind === 'property') navigate(`/properties/${a.attachedTo.id}`) }}
        dense
      />
    </SectionCard>
  )
}
